import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { Shield, Star, Globe, Users, Building2, Award, CheckCircle, ArrowRight } from 'lucide-react';

const RSS_LOGO = "https://customer-assets.emergentagent.com/job_199c36c2-e07b-4567-84a2-e99d8a5503d6/artifacts/c9vy7sfe_rumpun_silat_sarawak_logo_transparent_1024.png";

const registrations = [
  {
    act: 'Akta Pertubuhan 1966',
    no: 'PPM-010-13-02091987',
    date: '09 Februari 1987',
    color: '#D71920',
  },
  {
    act: 'Akta Pembangunan Sukan 1997',
    no: '4775/2011',
    date: '31 Oktober 2011',
    color: '#D4AF37',
  },
  {
    act: 'Pengiktirafan Badan Induk Rasmi',
    no: 'Kerajaan Negeri Sarawak',
    date: '08 Januari 2020',
    color: '#0B0B0B',
  },
];

const tujuan = [
  {
    num: '01',
    icon: <Users size={20} />,
    title: 'Menyatupadu Perguruan & Pesilat',
    desc: 'Menyatupadu dan mempererat hubungan di antara semua perguruan silat bumiputera dan pesilat di Sarawak.',
  },
  {
    num: '02',
    icon: <Shield size={20} />,
    title: 'Memaju Seni Silat Melayu',
    desc: 'Memperjuang, memaju dan menyelaraskan Seni Silat mempertahankan diri rumpun Melayu di Sarawak.',
  },
  {
    num: '03',
    icon: <Star size={20} />,
    title: 'Memelihara Warisan Bangsa',
    desc: 'Memelihara dan mempertahankan silat sebagai seni warisan bangsa yang perlu dijaga dan diwarisi.',
  },
  {
    num: '04',
    icon: <Award size={20} />,
    title: 'Membina Akhlak & Disiplin',
    desc: 'Membina akhlak, peribadi, disiplin, mutu dan daya tahan dalam setiap ahli dan pesilat.',
  },
  {
    num: '05',
    icon: <Globe size={20} />,
    title: 'Ketahanan Nasional',
    desc: 'Menjadikan silat sebagai asas ketahanan nasional yang kukuh dengan ahli bermaruah, bersatu-padu, sihat dan berdisiplin.',
  },
];

const facts = [
  { value: '168', label: 'Perguruan Gabungan', sub: 'Di seluruh Sarawak' },
  { value: '15,000', label: 'Anggaran Ahli', sub: 'Pesilat berdaftar' },
  { value: '13', label: 'Wilayah RSS', sub: 'Merentasi Sarawak' },
  { value: '38+', label: 'Tahun Berdiri', sub: 'Sejak 1987' },
];

export default function TentangRSS() {
  return (
    <PublicLayout>
      {/* Page Header */}
      <div className="bg-[#0B0B0B] text-white py-16">
        <div className="h-0.5 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920] -mt-16 mb-16" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs text-[#FFD21F] font-bold tracking-widest uppercase mb-3">
            Utama / Tentang RSS
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" data-testid="page-title">
            Tentang Rumpun Silat Sarawak
          </h1>
          <p className="text-[#999] mt-3 text-sm sm:text-base max-w-2xl leading-relaxed">
            Badan induk rasmi persilatan Negeri Sarawak yang menyatukan perguruan, guru, jurulatih dan pesilat seluruh negeri.
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-[#D71920] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:divide-x sm:divide-white/20">
            {facts.map((f, i) => (
              <div key={i} className="text-center sm:px-4">
                <div className="text-2xl font-extrabold text-[#FFD21F]">{f.value}</div>
                <div className="text-xs font-bold text-white mt-0.5">{f.label}</div>
                <div className="text-xs text-white/70">{f.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* About Content */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-14 items-start">

            {/* Left: Text */}
            <div className="lg:col-span-3">
              <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-4">Latar Belakang Organisasi</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B] mb-7 leading-tight">
                Siapakah Rumpun Silat Sarawak?
              </h2>
              <div className="space-y-5 text-[#555] leading-relaxed text-sm sm:text-base">
                <p>
                  Rumpun Silat Sarawak (RSS) ialah badan induk rasmi persilatan Negeri Sarawak yang
                  bertanggungjawab merancang, menyelaras dan membangunkan seni silat di peringkat negeri.
                  Ditubuhkan pada <strong className="text-[#0B0B0B]">9 Februari 1987</strong>, RSS telah
                  berkembang menjadi organisasi silat terbesar di Sarawak dengan lebih 168 perguruan gabungan
                  dan anggaran 15,000 orang ahli.
                </p>
                <p>
                  RSS diiktiraf oleh <strong className="text-[#0B0B0B]">Kerajaan Negeri Sarawak</strong> sebagai
                  Badan Induk Rasmi Persilatan Negeri Sarawak (2020) dan juga diiktiraf oleh
                  <strong className="text-[#0B0B0B]"> Persekutuan Silat Kebangsaan Malaysia (PESAKA)</strong>.
                  Penaung organisasi adalah YAB Premier Sarawak.
                </p>
                <p>
                  Melalui moto <em className="text-[#D4AF37] font-semibold not-italic">"Berseni Berbudi Berbakti"</em>,
                  RSS komited memastikan setiap pesilat tidak hanya mahir dalam seni pertahanan diri tetapi juga
                  memiliki nilai budi pekerti yang tinggi dan dedikasi kepada negara dan masyarakat.
                </p>
                <p>
                  RSS berperanan menyumbang idea, kepakaran, tenaga dan program ke arah pembangunan komuniti
                  yang berdisiplin dan berkualiti, selaras dengan aspirasi dan pembangunan negeri Sarawak.
                </p>
              </div>
            </div>

            {/* Right: Recognition Card */}
            <div className="lg:col-span-2">
              <div className="bg-[#0B0B0B] text-white rounded overflow-hidden">
                <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
                  <img src={RSS_LOGO} alt="RSS" className="h-10 w-10 object-contain" />
                  <div>
                    <div className="text-sm font-extrabold">Rumpun Silat Sarawak</div>
                    <div className="text-[#FFD21F] text-[10px] font-bold tracking-widest uppercase">BERSENI BERBUDI BERBAKTI</div>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {registrations.map((r, i) => (
                    <div key={i} className="border border-white/10 rounded p-4">
                      <div className="text-xs text-[#999] mb-1">{r.act}</div>
                      <div
                        className="text-sm font-bold mb-0.5"
                        style={{ color: r.color === '#0B0B0B' ? '#FFD21F' : r.color }}
                      >
                        {r.no}
                      </div>
                      <div className="text-xs text-[#777]">{r.date}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recognition badges */}
              <div className="mt-4 space-y-2">
                {[
                  { label: 'Diiktiraf PESAKA', sub: 'Persekutuan Silat Kebangsaan Malaysia' },
                  { label: 'Badan Induk Rasmi', sub: 'Kerajaan Negeri Sarawak' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#F7F7F7] border border-[#E5E5E5] rounded">
                    <CheckCircle size={16} className="text-[#16a34a] flex-shrink-0" />
                    <div>
                      <div className="text-xs font-bold text-[#0B0B0B]">{b.label}</div>
                      <div className="text-xs text-[#777]">{b.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Matlamat */}
      <section className="bg-[#F7F7F7] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Dasar Organisasi</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B] mb-4">Matlamat Utama RSS</h2>
            <p className="text-[#555] text-sm sm:text-base leading-relaxed">
              Menjadikan silat sebagai maruah kebangsaan dan ketahanan nasional yang kukuh dengan ahli yang
              bermaruah, bersatu-padu, sihat dan berdisiplin.
            </p>
          </div>

          <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-5">Tujuan Penubuhan</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tujuan.map((t, i) => (
              <div
                key={i}
                className="bg-white border border-[#E5E5E5] p-7 rounded hover:border-[#D71920]/30 hover:shadow-md transition-all group"
                data-testid={`tujuan-card-${i}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-10 h-10 bg-[#D71920]/8 rounded flex items-center justify-center text-[#D71920] group-hover:bg-[#D71920] group-hover:text-white transition-colors">
                    {t.icon}
                  </div>
                  <span className="text-xs font-extrabold text-[#E5E5E5] tracking-widest">{t.num}</span>
                </div>
                <h3 className="text-sm font-bold text-[#0B0B0B] mb-2 leading-tight">{t.title}</h3>
                <p className="text-xs text-[#777] leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visi Misi Motto */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Hala Tuju</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]">Visi, Misi & Motto</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                title: 'Visi',
                content: 'Menjadikan Rumpun Silat Sarawak sebagai persatuan silat yang disegani, tersusun dan diiktiraf di peringkat negeri, kebangsaan dan antarabangsa.',
                color: '#D71920',
              },
              {
                title: 'Misi',
                content: 'Menghimpunkan, mengurus dan membangunkan seluruh komuniti silat Sarawak melalui sistem pendaftaran yang sistematik, aktiviti yang berterusan dan pengurusan yang telus.',
                color: '#D4AF37',
              },
              {
                title: 'Motto',
                content: '"Berseni Berbudi Berbakti" — Menggambarkan pesilat yang berilmu seni, berbudi pekerti mulia dan setia kepada bangsa, negara dan persatuan.',
                color: '#0B0B0B',
              },
            ].map((item, i) => (
              <div key={i} className="border border-[#E5E5E5] p-8 rounded hover:shadow-md transition-shadow" data-testid={`vm-card-${i}`}>
                <div className="w-8 h-1 mb-5 rounded" style={{ backgroundColor: item.color }} />
                <h3 className="font-extrabold text-[#0B0B0B] text-lg mb-3">{item.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B0B0B] text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-4">Sertai Rumpun Silat Sarawak</h2>
          <p className="text-[#999] text-sm sm:text-base mb-8 max-w-xl mx-auto leading-relaxed">
            Daftar perguruan atau ahli pesilat anda untuk menjadi sebahagian daripada gerakan silat rasmi Negeri Sarawak.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/direktori-perguruan" className="bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-bold px-6 py-3 rounded transition-colors inline-flex items-center gap-2" data-testid="cta-direktori-btn">
              Direktori Perguruan <ArrowRight size={14} />
            </Link>
            <Link to="/register-school" className="border border-white/30 hover:border-white text-white text-sm font-semibold px-6 py-3 rounded transition-colors" data-testid="cta-daftar-btn">
              Daftar Perguruan
            </Link>
            <Link to="/kepimpinan" className="border border-white/30 hover:border-white text-white text-sm font-semibold px-6 py-3 rounded transition-colors" data-testid="cta-kepimpinan-btn">
              Lihat Kepimpinan
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
